import React from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { Row, Col, Flex, Card, Typography, theme } from '@app/ui';
import * as format from '../../utils/formatters';

import stadiumLogo from '@app/images/StadiumOutlined.svg';
import fmvLogo from '@app/images/fmv.svg';
import videoViewsLogo from '@app/images/video_views.svg';

const { Title, Text } = Typography;

const getVal = o => (o?.value ? parseInt(o.value, 10) : 0);
const toInt = n => (n ? parseInt(n, 10) : 0);

const TotalCards = ({ cards, client }) => {
    const { t } = useTranslation('home');
    const {
        token: { colorText },
    } = theme.useToken();
    const {
        currency: { code: currency },
    } = client;
    const { isLoading, hasError } = cards;

    const cardData = [
        {
            title: t('totalCards.topPlatform'),
            value: format.shortCurrency(getVal(cards.topPlatform), 1, currency),
            sub: cards.topPlatform?.name,
            icon: (
                <>
                    <i
                        className="fa-regular fa-grid-2"
                        style={{
                            color: '#009FDF',
                            fontSize: 16,
                        }}
                    />
                </>
            ),
        },

        {
            title: t('totalCards.topPlacement'),
            value: format.shortCurrency(
                getVal(cards.topPlacement),
                1,
                currency,
            ),
            sub: cards.topPlacement?.name,
            icon: (
                <>
                    <img className="logo" src={stadiumLogo} />
                </>
            ),
        },
        {
            title: t('totalCards.impressions'),
            value: format.shortDecimal(toInt(cards.totalImpressions), 1),
            icon: (
                <>
                    <i
                        className="fa-regular fa-eye"
                        style={{
                            color: '#009FDF',
                            fontSize: 16,
                        }}
                    />
                </>
            ),
        },
        {
            title: t('totalCards.videoViews'),
            value: format.shortDecimal(toInt(cards.totalVideoViews), 1),
            icon: (
                <>
                    <img className="logo" src={videoViewsLogo} />
                </>
            ),
        },
        {
            title: t('totalCards.totalFmv'),
            value: format.shortCurrency(
                toInt(cards.totalFullMediaValue),
                1,
                currency,
            ),
            icon: (
                <>
                    <img className="logo" src={fmvLogo} />
                </>
            ),
        },
        {
            title: t('totalCards.totalSmv'),
            value: format.shortCurrency(
                toInt(cards.totalSpMediaValue),
                1,
                currency,
            ),
            icon: (
                <>
                    <i
                        className="fa-solid fa-dollar-sign"
                        style={{
                            color: '#009FDF',
                            fontSize: 16,
                        }}
                    />
                </>
            ),
        },
    ];

    if (hasError && !isLoading) {
        return (
            <Card bordered data-test="total-cards-error">
                <Flex
                    justify="center"
                    align="center"
                    style={{
                        flexDirection: 'column',
                        gap: 8,
                        minHeight: 220,
                    }}
                >
                    <i
                        className="fa-regular fa-circle-exclamation"
                        style={{ color: colorText, fontSize: 24 }}
                    />
                    <Text>{t('overview.loadError')}</Text>
                </Flex>
            </Card>
        );
    }

    return (
        <Row gutter={[16, 24]}>
            {cardData.map(({ title, value, icon, sub }, index) => (
                <Col key={index} span={12}>
                    <Card loading={isLoading} bordered>
                        <Flex justify="space-between" dir="row">
                            <Text style={{ margin: 0 }}>{title}</Text>
                            <Flex
                                justify="center"
                                align="center"
                                style={{
                                    border: '1px solid #009FDF',
                                    height: 32,
                                    width: 32,
                                    borderRadius: 4,
                                }}
                            >
                                {icon}
                            </Flex>
                        </Flex>
                        <div
                            style={{
                                height: 24,
                                display: 'flex',
                                justifyContent: 'flex-start',
                                alignItems: 'center',
                            }}
                        >
                            {sub && (
                                <Text
                                    style={{
                                        fontWeight: 700,
                                        color: '#1f1f1f',
                                    }}
                                >
                                    {sub}
                                </Text>
                            )}
                        </div>
                        <Title
                            level={1}
                            style={{
                                margin: 0,
                                fontWeight: 400,
                                color: '#1f1f1f',
                            }}
                        >
                            {value}
                        </Title>
                    </Card>
                </Col>
            ))}
        </Row>
    );
};

TotalCards.propTypes = {
    cards: PropTypes.object.isRequired,
    client: PropTypes.object.isRequired,
};

export default TotalCards;
